import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import {
  cardHover,
  fadeIn,
  glassCard,
  iconHover,
  pageTransition,
  slideUp,
  staggerContainer,
  textReveal,
} from '@/lib/animations';
import { motion } from 'framer-motion';
import { Clock, Eye, Plus } from 'lucide-react';

const AiConsult = () => {
  const clinicalInsights = [
    {
      name: 'John Smith',
      age: 45,
      condition: 'Dry Eye Syndrome',
      symptoms: ['Eye irritation', 'Blurred vision', 'Light sensitivity'],
      severity: 'moderate',
      confidence: 92,
    },
    {
      name: 'Sarah Johnson',
      age: 62,
      condition: 'Early Cataract',
      symptoms: ['Cloudy vision', 'Difficulty with night driving'],
      severity: 'mild',
      confidence: 88,
    },
  ];

  const treatmentPlans = [
    {
      patient: 'Michael Brown',
      condition: 'Dry Eye Syndrome',
      treatments: [
        'Prescribe artificial tears (Refresh Optive)',
        'Recommend warm compress therapy',
        'Suggest environmental modifications',
      ],
      status: 'pending',
    },
    {
      patient: 'Emma Wilson',
      condition: 'Early Cataract',
      treatments: [
        'Monitor progression',
        'Update prescription',
        'Schedule follow-up in 6 months',
      ],
      status: 'approved',
    },
  ];

  const patientEducation = [
    {
      patient: 'David Lee',
      topic: 'Understanding Dry Eye',
      description:
        'Comprehensive guide to dry eye causes, symptoms, and management',
      status: 'ready',
    },
    {
      patient: 'Lisa Anderson',
      topic: 'Cataract Development',
      description: 'What to expect with early cataract and treatment options',
      status: 'ready',
    },
  ];

  const riskMonitoring = [
    {
      name: 'Robert Chen',
      age: 68,
      condition: 'Glaucoma Risk',
      tags: ['Age', 'Family History', 'Previous Eye Surgery'],
      riskLevel: 'High Risk',
      nextFollowUp: '3 months',
    },
    {
      name: 'Maria Garcia',
      age: 55,
      condition: 'Retinal Health',
      tags: ['Diabetes', 'High Blood Pressure'],
      riskLevel: 'Moderate Risk',
      nextFollowUp: '6 months',
    },
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'mild':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20';
      case 'moderate':
        return 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-500/20';
      case 'high':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-500/20';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20';
      case 'pending':
        return 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-500/20';
      case 'ready':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-500/20';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'High Risk':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20';
      case 'Moderate Risk':
        return 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-500/20';
      case 'Low Risk':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-500/20';
    }
  };

  return (
    <Layout>
      <motion.div className="w-full space-y-8" {...pageTransition}>
        {/* Header */}
        <motion.div variants={fadeIn} initial="initial" animate="animate">
          <motion.h1
            className="text-3xl font-bold text-slate-900 dark:text-white mb-2"
            variants={textReveal}
          >
            Ai Consult
          </motion.h1>
          <motion.p
            className="text-slate-700 dark:text-gray-300"
            variants={textReveal}
          >
            Your clinical co-pilot for evidence-based care decisions
          </motion.p>
        </motion.div>

        {/* Clinical Insights */}
        <motion.div variants={glassCard} initial="initial" animate="animate">
          <GlassCard>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <motion.h2
                  className="text-xl font-semibold text-slate-900 dark:text-white"
                  variants={textReveal}
                >
                  Clinical Insights
                </motion.h2>
                <motion.div whileHover={iconHover}>
                  <Button className="bg-gradient-to-r from-purple-500 to-blue-500 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    New Analysis
                  </Button>
                </motion.div>
              </div>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                {clinicalInsights.map((insight, index) => (
                  <motion.div
                    key={index}
                    variants={slideUp}
                    whileHover={cardHover}
                  >
                    <GlassCard>
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <motion.h3
                              className="text-slate-900 dark:text-white font-medium"
                              variants={textReveal}
                            >
                              {insight.name}
                            </motion.h3>
                            <motion.p
                              className="text-slate-600 dark:text-gray-400 text-sm"
                              variants={textReveal}
                            >
                              Age: {insight.age}
                            </motion.p>
                          </div>
                          <div className="text-right">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${getSeverityColor(
                                insight.severity
                              )}`}
                            >
                              {insight.severity}
                            </span>
                            <motion.p
                              className="text-slate-600 dark:text-gray-400 text-xs mt-1"
                              variants={textReveal}
                            >
                              {insight.confidence}% confidence
                            </motion.p>
                          </div>
                        </div>
                        <motion.h4
                          className="text-slate-900 dark:text-white font-medium mb-2"
                          variants={textReveal}
                        >
                          {insight.condition}
                        </motion.h4>
                        <div className="flex flex-wrap gap-1">
                          {insight.symptoms.map((symptom, idx) => (
                            <motion.span
                              key={idx}
                              whileHover={iconHover}
                              className="px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 text-xs rounded"
                            >
                              {symptom}
                            </motion.span>
                          ))}
                        </div>
                      </div>
                    </GlassCard>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Treatment Plans and Patient Education */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {/* Treatment Plans */}
          <motion.div variants={glassCard}>
            <GlassCard>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <motion.h2
                    className="text-xl font-semibold text-slate-900 dark:text-white"
                    variants={textReveal}
                  >
                    Treatment Plans
                  </motion.h2>
                  <motion.div whileHover={iconHover}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-purple-600 dark:text-purple-400 border-purple-600 dark:border-purple-400"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Plan
                    </Button>
                  </motion.div>
                </div>
                <motion.div
                  className="space-y-4"
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                >
                  {treatmentPlans.map((plan, index) => (
                    <motion.div
                      key={index}
                      variants={slideUp}
                      whileHover={cardHover}
                    >
                      <GlassCard>
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <motion.h3
                                className="text-slate-900 dark:text-white font-medium"
                                variants={textReveal}
                              >
                                {plan.patient}
                              </motion.h3>
                              <motion.p
                                className="text-slate-600 dark:text-gray-400 text-sm"
                                variants={textReveal}
                              >
                                {plan.condition}
                              </motion.p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                                plan.status
                              )}`}
                            >
                              {plan.status}
                            </span>
                          </div>
                          <ul className="space-y-1">
                            {plan.treatments.map((treatment, idx) => (
                              <motion.li
                                key={idx}
                                variants={slideUp}
                                className="text-slate-700 dark:text-gray-300 text-sm flex items-start"
                              >
                                <span className="text-blue-600 dark:text-blue-400 mr-2">
                                  •
                                </span>
                                {treatment}
                              </motion.li>
                            ))}
                          </ul>
                        </div>
                      </GlassCard>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </GlassCard>
          </motion.div>

          {/* Patient Education */}
          <motion.div variants={glassCard}>
            <GlassCard>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <motion.h2
                    className="text-xl font-semibold text-slate-900 dark:text-white"
                    variants={textReveal}
                  >
                    Patient Education
                  </motion.h2>
                  <motion.div whileHover={iconHover}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 dark:text-green-400 border-green-600 dark:border-green-400"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Generate
                    </Button>
                  </motion.div>
                </div>
                <motion.div
                  className="space-y-4"
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                >
                  {patientEducation.map((education, index) => (
                    <motion.div
                      key={index}
                      variants={slideUp}
                      whileHover={cardHover}
                    >
                      <GlassCard>
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <motion.h3
                                className="text-slate-900 dark:text-white font-medium"
                                variants={textReveal}
                              >
                                {education.patient}
                              </motion.h3>
                              <motion.p
                                className="text-blue-600 dark:text-blue-400 text-sm"
                                variants={textReveal}
                              >
                                {education.topic}
                              </motion.p>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                                education.status
                              )}`}
                            >
                              {education.status}
                            </span>
                          </div>
                          <motion.p
                            className="text-slate-700 dark:text-gray-300 text-sm mb-3"
                            variants={textReveal}
                          >
                            {education.description}
                          </motion.p>
                          <div className="flex items-center space-x-2 text-sm">
                            <Eye className="w-4 h-4 text-slate-600 dark:text-gray-400" />
                            <span className="text-slate-600 dark:text-gray-400">
                              Digital Handout
                            </span>
                            <motion.div whileHover={iconHover}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 p-0 h-auto"
                              >
                                Printable PDF
                              </Button>
                            </motion.div>
                          </div>
                        </div>
                      </GlassCard>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </GlassCard>
          </motion.div>
        </motion.div>

        {/* Risk Monitoring */}
        <motion.div variants={glassCard} initial="initial" animate="animate">
          <GlassCard>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <motion.h2
                  className="text-xl font-semibold text-slate-900 dark:text-white"
                  variants={textReveal}
                >
                  Risk Monitoring
                </motion.h2>
                <motion.div whileHover={iconHover}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-orange-600 dark:text-orange-400 border-orange-600 dark:border-orange-400"
                  >
                    View All
                  </Button>
                </motion.div>
              </div>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                {riskMonitoring.map((patient, index) => (
                  <motion.div
                    key={index}
                    variants={slideUp}
                    whileHover={cardHover}
                  >
                    <GlassCard>
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <motion.h3
                              className="text-slate-900 dark:text-white font-medium"
                              variants={textReveal}
                            >
                              {patient.name}
                            </motion.h3>
                            <motion.p
                              className="text-slate-600 dark:text-gray-400 text-sm"
                              variants={textReveal}
                            >
                              Age: {patient.age}
                            </motion.p>
                          </div>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${getRiskColor(
                              patient.riskLevel
                            )}`}
                          >
                            {patient.riskLevel}
                          </span>
                        </div>
                        <motion.h4
                          className="text-slate-900 dark:text-white font-medium mb-2"
                          variants={textReveal}
                        >
                          {patient.condition}
                        </motion.h4>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {patient.tags.map((tag, idx) => (
                            <motion.span
                              key={idx}
                              whileHover={iconHover}
                              className="px-2 py-1 bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 text-xs rounded"
                            >
                              {tag}
                            </motion.span>
                          ))}
                        </div>
                        <div className="flex items-center text-slate-600 dark:text-gray-400 text-sm">
                          <Clock className="w-4 h-4 mr-1" />
                          Next follow-up: {patient.nextFollowUp}
                        </div>
                      </div>
                    </GlassCard>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </GlassCard>
        </motion.div>
      </motion.div>
    </Layout>
  );
};

export default AiConsult;
